import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabase';
import { NEWS_CATEGORIES, NEWS_STATUSES, getNewsStatusLabel } from '../../data/newsConfig';
import SafeIcon from '../../common/SafeIcon';
import ImageWithFallback from '../../components/ImageWithFallback';
import ImagePicker from '../../components/ImagePicker';
import NewsRelationsEditor from '../../components/admin/NewsRelationsEditor';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPlus, FiX, FiEdit, FiTrash2, FiStar, FiCalendar, FiExternalLink } = FiIcons;

const toLocalDateTimeInput = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const emptyItem = () => ({
  title: '', excerpt: '', content: '', published_at: toLocalDateTimeInput(new Date()), category: 'Ανακοινώσεις',
  image: '', image_url: '', source_url: '', facebook_url: '', status: 'draft', is_featured: false, team_categories: [],
});

const News = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyItem());
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [message, setMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchNews = async () => {
    setLoading(true);
    const { data } = await supabase.from('news_aox1234').select('*').order('is_featured', { ascending: false }).order('published_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchNews(); }, []);

  const reset = () => {
    setFormData(emptyItem());
    setSelectedPlayerIds([]);
    setSelectedMatchIds([]);
    setTeamCategories([]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = async (item) => {
    const [playersResult, matchesResult] = await Promise.all([
      supabase.from('news_players_aox1234').select('player_id').eq('news_id', item.id),
      supabase.from('news_matches_aox1234').select('match_id').eq('news_id', item.id),
    ]);
    setFormData({ ...emptyItem(), ...item, published_at: toLocalDateTimeInput(item.published_at), image: item.image || item.image_url || '', image_url: item.image_url || item.image || '' });
    setSelectedPlayerIds((playersResult.data || []).map((row) => row.player_id));
    setSelectedMatchIds((matchesResult.data || []).map((row) => row.match_id));
    setTeamCategories(Array.isArray(item.team_categories) ? item.team_categories : []);
    setEditingId(item.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const syncRelations = async (newsId) => {
    await Promise.all([
      supabase.from('news_players_aox1234').delete().eq('news_id', newsId),
      supabase.from('news_matches_aox1234').delete().eq('news_id', newsId),
    ]);
    if (selectedPlayerIds.length) {
      const { error } = await supabase.from('news_players_aox1234').insert(selectedPlayerIds.map((playerId) => ({ news_id: newsId, player_id: playerId })));
      if (error) throw error;
    }
    if (selectedMatchIds.length) {
      const { error } = await supabase.from('news_matches_aox1234').insert(selectedMatchIds.map((matchId) => ({ news_id: newsId, match_id: matchId })));
      if (error) throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const publishDate = new Date(formData.published_at);
      if (Number.isNaN(publishDate.getTime())) throw new Error('Μη έγκυρη ημερομηνία.');
      const imageUrl = formData.image || formData.image_url || null;
      const payload = {
        title: formData.title.trim(), excerpt: formData.excerpt?.trim() || null, content: formData.content.trim(),
        published_at: publishDate.toISOString(), category: formData.category, image: imageUrl, image_url: imageUrl,
        source_url: formData.source_url?.trim() || null, facebook_url: formData.facebook_url?.trim() || null,
        status: formData.status, is_featured: Boolean(formData.is_featured), team_categories: teamCategories,
        updated_at: new Date().toISOString(),
      };

      let newsId = editingId;
      if (editingId) {
        const { error } = await supabase.from('news_aox1234').update(payload).eq('id', editingId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('news_aox1234').insert([{ ...payload, created_at: new Date().toISOString() }]).select('id').single();
        if (error) throw error;
        newsId = data.id;
      }
      await syncRelations(newsId);
      setMessage(editingId ? 'Η είδηση και οι συνδέσεις ενημερώθηκαν.' : 'Η είδηση και οι συνδέσεις δημιουργήθηκαν.');
      reset();
      await fetchNews();
    } catch (err) {
      setMessage(`Σφάλμα: ${err.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Διαγραφή της είδησης;')) return;
    const { error } = await supabase.from('news_aox1234').delete().eq('id', id);
    if (error) setMessage(`Σφάλμα: ${error.message}`);
    else { setMessage('Η είδηση διαγράφηκε.'); await fetchNews(); }
  };

  const filtered = useMemo(() => items.filter((item) => filterStatus === 'all' || (item.status || 'published') === filterStatus), [items, filterStatus]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm"><div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3"><button onClick={() => navigate('/admin/dashboard')} className="rounded-md p-2 text-gray-500 hover:bg-gray-100"><SafeIcon icon={FiArrowLeft} className="h-5 w-5" /></button><div><h1 className="text-lg font-semibold">Νέα & Ανακοινώσεις</h1><p className="text-xs text-gray-500">Νέα συνδεδεμένα με ομάδες, παίκτριες και αγώνες</p></div></div>
        <button onClick={() => showForm ? reset() : setShowForm(true)} className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-white"><SafeIcon icon={showForm ? FiX : FiPlus} className="mr-2 h-4 w-4" />{showForm ? 'Ακύρωση' : 'Προσθήκη Είδησης'}</button>
      </div></header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {message && <div className="mb-6 rounded-lg border bg-white p-4 text-sm text-gray-700">{message}</div>}

        {showForm && <form onSubmit={handleSubmit} className="mb-8 space-y-6 rounded-2xl bg-white p-6 shadow-md">
          <h2 className="text-xl font-black">{editingId ? 'Επεξεργασία Είδησης' : 'Νέα Είδηση'}</h2>
          <div><label className="mb-1 block text-sm font-bold">Τίτλος *</label><input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full rounded-md border px-3 py-2" /></div>
          <div><label className="mb-1 block text-sm font-bold">Σύντομη περιγραφή</label><textarea rows="2" value={formData.excerpt || ''} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} className="w-full rounded-md border px-3 py-2" /></div>
          <div className="grid gap-4 md:grid-cols-3">
            <div><label className="mb-1 block text-sm font-bold">Κατάσταση</label><select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full rounded-md border px-3 py-2">{NEWS_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
            <div><label className="mb-1 block text-sm font-bold">Ημερομηνία / Ώρα</label><input type="datetime-local" required value={formData.published_at} onChange={(e) => setFormData({ ...formData, published_at: e.target.value })} className="w-full rounded-md border px-3 py-2" /></div>
            <div><label className="mb-1 block text-sm font-bold">Κατηγορία</label><select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full rounded-md border px-3 py-2">{NEWS_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
          </div>
          <label className="flex items-center gap-3 rounded-lg border bg-yellow-50 p-4"><input type="checkbox" checked={Boolean(formData.is_featured)} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} /><SafeIcon icon={FiStar} className="h-5 w-5 text-yellow-600" /><span className="font-bold">Featured είδηση</span></label>
          <div><label className="mb-1 block text-sm font-bold">Περιεχόμενο *</label><textarea rows="9" required value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full rounded-md border px-3 py-2" /></div>
          <div><label className="mb-2 block text-sm font-bold">Εικόνα</label><ImagePicker value={formData.image || formData.image_url} onChange={(url) => setFormData({ ...formData, image: url, image_url: url })} folder="news" /></div>
          <NewsRelationsEditor selectedPlayerIds={selectedPlayerIds} setSelectedPlayerIds={setSelectedPlayerIds} selectedMatchIds={selectedMatchIds} setSelectedMatchIds={setSelectedMatchIds} teamCategories={teamCategories} setTeamCategories={setTeamCategories} />
          <div className="grid gap-4 md:grid-cols-2"><div><label className="mb-1 block text-sm font-bold">URL πηγής</label><input type="url" value={formData.source_url || ''} onChange={(e) => setFormData({ ...formData, source_url: e.target.value })} className="w-full rounded-md border px-3 py-2" /></div><div><label className="mb-1 block text-sm font-bold">Facebook URL</label><input type="url" value={formData.facebook_url || ''} onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })} className="w-full rounded-md border px-3 py-2" /></div></div>
          <div className="flex justify-end gap-3"><button type="button" onClick={reset} className="rounded-md border px-4 py-2">Ακύρωση</button><button type="submit" className="rounded-md bg-primary-600 px-5 py-2 font-bold text-white">Αποθήκευση</button></div>
        </form>}

        <section className="rounded-2xl bg-white p-6 shadow-md">
          <div className="mb-6 flex flex-col justify-between gap-3 md:flex-row md:items-center"><h2 className="text-xl font-black">Λίστα Ειδήσεων</h2><select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-md border px-3 py-2 text-sm"><option value="all">Όλες</option>{NEWS_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></div>
          {loading ? <div className="py-10 text-center text-gray-500">Φόρτωση...</div> : <div className="space-y-5">{filtered.map((item) => (
            <article key={item.id} className="flex flex-col gap-5 border-b pb-5 last:border-0 md:flex-row">
              <div className="h-32 w-full flex-shrink-0 overflow-hidden rounded-xl bg-gray-100 md:w-48"><ImageWithFallback src={item.image || item.image_url} alt={item.title} className="h-full w-full object-cover" /></div>
              <div className="min-w-0 flex-1"><div className="mb-2 flex flex-wrap gap-2"><span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-bold text-primary-700">{item.category}</span><span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold">{getNewsStatusLabel(item.status, item.published_at)}</span>{item.is_featured && <span className="rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-bold text-yellow-800">Featured</span>}{(item.team_categories || []).map((t) => <span key={t} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">{t}</span>)}</div><h3 className="text-lg font-black">{item.title}</h3><p className="mt-2 text-sm text-gray-600">{item.excerpt || (item.content || '').slice(0, 150)}</p><div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500"><span className="inline-flex items-center"><SafeIcon icon={FiCalendar} className="mr-1 h-3.5 w-3.5" />{new Date(item.published_at).toLocaleString('el-GR')}</span>{item.source_url && <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary-600"><SafeIcon icon={FiExternalLink} className="mr-1 h-3.5 w-3.5" />Πηγή</a>}</div></div>
              <div className="flex gap-2"><button onClick={() => handleEdit(item)} className="rounded-lg bg-blue-50 p-2 text-blue-700"><SafeIcon icon={FiEdit} className="h-4 w-4" /></button><button onClick={() => handleDelete(item.id)} className="rounded-lg bg-red-50 p-2 text-red-700"><SafeIcon icon={FiTrash2} className="h-4 w-4" /></button></div>
            </article>
          ))}</div>}
        </section>
      </main>
    </div>
  );
};

export default News;
